import { securedAxiosInstance, plainAxiosInstance } from "../axios";
import { toSnakeCase, toCamelCase } from "@estaly/ui";

class AnalyticsService {
  async fetchRetailData(
    endpoint,
    startDate,
    endDate,
    ownershipTypes = [],
    regions = [],
    sortBy = null,
    sortDirection = null,
  ) {
    const params = {
      start: startDate,
      end: endDate,
      ownershipType: ownershipTypes,
      region: regions,
    };
    if (sortBy) params.sortBy = sortBy;
    if (sortDirection) params.sortDirection = sortDirection;
    const response = await securedAxiosInstance.get(endpoint, {
      params: toSnakeCase(params),
    });
    return toCamelCase(response);
  }

  getRetailStoresData(startDate, endDate, ownershipTypes = [], regions = [], sortBy = null, sortDirection = null) {
    return this.fetchRetailData(
      "/admin/analytics/retail/stores",
      startDate,
      endDate,
      ownershipTypes,
      regions,
      sortBy,
      sortDirection,
    );
  }

  getRetailRegionsData(startDate, endDate, ownershipTypes = [], regions = [], sortBy = null, sortDirection = null) {
    return this.fetchRetailData(
      "/admin/analytics/retail/regions",
      startDate,
      endDate,
      ownershipTypes,
      regions,
      sortBy,
      sortDirection,
    );
  }

  async getRetailSellersData(storeId, startDate, endDate, sortBy = null, sortDirection = null) {
    const params = { start: startDate, end: endDate, storeId };
    if (sortBy) params.sortBy = sortBy;
    if (sortDirection) params.sortDirection = sortDirection;
    const response = await securedAxiosInstance.get("/admin/analytics/retail/sellers", {
      params: toSnakeCase(params),
    });
    return toCamelCase(response);
  }

  getGeneralAnalytics(params) {
    return securedAxiosInstance.get("/admin/analytics", {
      params: toSnakeCase(params),
    });
  }

  computeOrganizationInvoicesAnalytics(params) {
    return plainAxiosInstance.get("/analytics/invoices", {
      params: toSnakeCase(params),
    });
  }

  computeOrganizationRemunerationsAnalytics(params) {
    return plainAxiosInstance.get("/analytics/remunerations", {
      params: toSnakeCase(params),
    });
  }

  computeOrganizationClaimsAnalytics(params) {
    return plainAxiosInstance.get("/analytics/claims", {
      params: toSnakeCase(params),
    });
  }

  exportRetailCsv(endpoint, params) {
    return securedAxiosInstance.get(endpoint, {
      params: toSnakeCase(params),
      responseType: "blob",
    });
  }

  buildRetailParams(
    startDate,
    endDate,
    ownershipTypes = [],
    regions = [],
    sortBy = null,
    sortDirection = null,
    includeColumns = [],
  ) {
    const params = {
      start: startDate,
      end: endDate,
      ownershipType: ownershipTypes,
      region: regions,
      includeColumns,
    };
    if (sortBy) params.sortBy = sortBy;
    if (sortDirection) params.sortDirection = sortDirection;
    return params;
  }

  exportRetailStores(...args) {
    return this.exportRetailCsv("/admin/analytics/retail/export_stores", this.buildRetailParams(...args));
  }

  exportRetailRegions(...args) {
    return this.exportRetailCsv("/admin/analytics/retail/export_regions", this.buildRetailParams(...args));
  }

  exportRetailSellers(storeId, startDate, endDate, sortBy = null, sortDirection = null, includeColumns = []) {
    const params = { start: startDate, end: endDate, storeId, includeColumns };
    if (sortBy) params.sortBy = sortBy;
    if (sortDirection) params.sortDirection = sortDirection;
    return this.exportRetailCsv("/admin/analytics/retail/export_sellers", params);
  }

  getRoadmapPerformance(storeIds = [], dataSource = "plan_sales") {
    const params = { data_source: dataSource };
    if (storeIds.length > 0) {
      params.store_ids = storeIds;
    }
    return securedAxiosInstance.get("/analytics/roadmap_performance", { params });
  }

  getRegionalRoadmapPerformance(dataSource = "plan_sales", region = null) {
    const params = { data_source: dataSource };
    if (region) params.region = region;
    return securedAxiosInstance.get("/analytics/regional_roadmap_performance", { params });
  }

  getAdminRoadmapPerformance(dataSource = "plan_sales") {
    return securedAxiosInstance.get("/analytics/admin_roadmap_performance", {
      params: { data_source: dataSource },
    });
  }

  downloadRegionalRoadmapPdf(dataSource = "plan_sales", region = null) {
    const params = { data_source: dataSource };
    if (region) params.region = region;
    return securedAxiosInstance.get("/analytics/regional_roadmap_performance.pdf", {
      params,
      responseType: "blob",
    });
  }
}

export default new AnalyticsService();
